import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Smartphone,
  Activity,
  Calendar,
  User,
} from "lucide-react";
import InstanceCard from "../components/InstanceCard";
import AddInstanceModal from "../components/AddInstanceModal";
import PaymentModal from "../components/PaymentModal";
import { getInstances } from "../apis/instance";
import RenewalPaymentModal from "../components/RenewalPaymentModal";

const InstancesPage = () => {
  const [instances, setInstances] = useState([]);
  const [showInstanceModal, setShowInstanceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({});
  const [instanceName, setInstanceName] = useState("");

  // Renewal states
  const [showRenewalPaymentModal, setShowRenewalPaymentModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);

  const navigate = useNavigate();

  const fetchUserInfo = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setUserInfo({
      name: user.name || "User",
      email: user.email || "",
      phone: user.phone || "",
    });
  };

  const fetchInstances = async () => {
    try {
      setIsLoading(true);
      const { data } = await getInstances();
      setInstances(data.instances || []);
    } catch (err) {
      console.error("Failed to fetch instances", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenewClick = (instance) => {
    setSelectedInstance(instance);
    setShowRenewalPaymentModal(true);
  };

  const handleRenewalSuccess = () => {
    setShowRenewalPaymentModal(false);
    setSelectedInstance(null);
    fetchInstances();
  };

  const handleAddInstanceClick = () => setShowInstanceModal(true);

  const handleInstanceModalSubmit = (name) => {
    setInstanceName(name);
    setShowInstanceModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setInstanceName("");
    fetchInstances();
  };

  const handleInstanceModalClose = () => {
    setShowInstanceModal(false);
    setInstanceName("");
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setInstanceName("");
  };

  const handleCardClick = (instance) => {
    navigate(`/instance/${instance.instance_id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    fetchInstances();
    fetchUserInfo();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, duration: 0.3 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Stats cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Total Instances</p>
                <motion.p
                  className="text-xl lg:text-2xl font-extrabold text-gray-900"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 420 }}
                >
                  {instances.length}
                </motion.p>
              </div>
              <div className="p-2 rounded-xl ring-1 ring-gray-200 bg-[#01CC54]/10">
                <Smartphone size={18} className="text-[#01CC54]" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Active Instances</p>
                <motion.p
                  className="text-xl lg:text-2xl font-extrabold text-[#01CC54]"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.25, type: "spring", stiffness: 420 }}
                >
                  {instances.filter((i) => i.status === "Connected").length}
                </motion.p>
              </div>
              <div className="p-2 rounded-xl ring-1 ring-gray-200 bg-primary/10">
                <Activity size={18} className="text-primary" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
            variants={itemVariants}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Pending Setup</p>
                <motion.p
                  className="text-xl lg:text-2xl font-extrabold text-amber-600"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 420 }}
                >
                  {instances.filter((i) => i.status === "Pending").length}
                </motion.p>
              </div>
              <div className="p-2 rounded-xl ring-1 ring-gray-200 bg-amber-100">
                <Calendar size={18} className="text-amber-600" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Header Section */}
        <motion.div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <div className="flex items-center mb-3 sm:mb-0">
            <div className="rounded-full p-2 mr-3 ring-1 ring-gray-200 bg-[#01CC54]/10">
              <User size={18} className="text-[#01CC54]" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-extrabold text-gray-900 mb-0.5">
                WhatsApp Instances
              </h1>
              <p className="text-sm text-gray-600">
                Welcome back, {userInfo.name}! Manage your WhatsApp business instances
              </p>
            </div>
          </div>

          <motion.button
            onClick={handleAddInstanceClick}
            className="inline-flex items-center px-4 py-2 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-sm transition text-sm"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            <Plus size={16} className="mr-1.5" />
            Create New Instance
          </motion.button>
        </motion.div>

        {/* Instances Grid */}
        <AnimatePresence>
          {isLoading ? (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse"
                >
                  <div className="h-3 bg-gray-200 rounded mb-3"></div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </motion.div>
          ) : instances.length === 0 ? (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <div className="rounded-xl border border-gray-200 bg-white p-6 max-w-lg mx-auto shadow-sm">
                <div className="rounded-full p-4 w-fit mx-auto mb-4 ring-1 ring-gray-200 bg-gray-50">
                  <Smartphone size={48} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Get Started Today
                </h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Create your first WhatsApp instance to start managing your business communications. Each instance requires a separate subscription.
                </p>
                <motion.button
                  onClick={handleAddInstanceClick}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 shadow-sm transition inline-flex items-center text-sm"
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Plus size={18} className="mr-2" />
                  Create Your First Instance
                </motion.button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {instances.map((instance, index) => (
                <motion.div
                  key={instance.instance_id}
                  variants={itemVariants}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06, duration: 0.45 }}
                >
                  <InstanceCard
                    instance={instance}
                    onClick={handleCardClick}
                    onRenewClick={handleRenewClick}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modals */}
        <AddInstanceModal
          open={showInstanceModal}
          onClose={handleInstanceModalClose}
          onSubmit={handleInstanceModalSubmit}
        />

        <PaymentModal
          open={showPaymentModal}
          onClose={handlePaymentModalClose}
          onPaymentSuccess={handlePaymentSuccess}
          instanceName={instanceName}
          userInfo={userInfo}
        />

        {selectedInstance && (
          <RenewalPaymentModal
            open={showRenewalPaymentModal}
            onClose={() => setShowRenewalPaymentModal(false)}
            onPaymentSuccess={handleRenewalSuccess}
            instanceId={selectedInstance.instance_id}
            instanceName={selectedInstance.name}
            userInfo={userInfo}
          />
        )}
      </div>
    </div>
  );
};

export default InstancesPage;
